import React, { useEffect, useState } from "react";
import "./styles/dashboard.css";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import CommonHeader from "./components/CommonHeader";
import { hasPermission } from "./utils/permissionUtils";

const AgentStatement = () => {
  const navigate = useNavigate();

  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingAmount, setEditingAmount] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkAmounts, setBulkAmounts] = useState({});

  useEffect(() => {
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = new URLSearchParams();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/agentStatement/getAgencies`, {
        method: 'POST',
        headers: {
          'Authorization': token
        },
        body: data
      });

      const result = await response.json();
      if (result.status) {
        setAgencies(result.agencies);
      } else {
        toast.error(result.message || 'Failed to fetch agencies');
      }
    } catch (error) {
      console.error('Error fetching agencies:', error);
      toast.error('Failed to fetch agencies');
    }
  };

  const handleGenerateStatement = async () => {
    if (!selectedAgency || !fromDate || !toDate) {
      toast.error('Please select agency and date range');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const data = new URLSearchParams();
      data.append('agency_id', selectedAgency);
      data.append('from_date', fromDate);
      data.append('to_date', toDate);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/agentStatement/getAgentStatement`, {
        method: 'POST',
        headers: {
          'Authorization': token
        },
        body: data
      });

      const result = await response.json();
      if (result.status) {
        setStatement(result);
        // Initialize bulk amounts for editing
        const amounts = {};
        result.statement.forEach(booking => {
          amounts[booking.booking_id] = booking.amount;
        });
        setBulkAmounts(amounts);
      } else {
        toast.error(result.message || 'Failed to generate statement');
      }
    } catch (error) {
      console.error('Error generating statement:', error);
      toast.error('Failed to generate statement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAmount = async (bookingId, amount) => {
    try {
      const token = localStorage.getItem('token');
      const data = new URLSearchParams();
      data.append('booking_id', bookingId);
      data.append('amount', amount);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/agentStatement/updateAmount`, {
        method: 'POST',
        headers: {
          'Authorization': token
        },
        body: data
      });

      const result = await response.json();
      if (result.status) {
        toast.success('Amount updated successfully');
        // Update local state
        setStatement(prev => ({
          ...prev,
          statement: prev.statement.map(booking => 
            booking.booking_id === bookingId 
              ? { ...booking, amount: parseFloat(amount) }
              : booking
          )
        }));
        setEditingAmount(null);
        setEditAmount('');
      } else {
        toast.error(result.message || 'Failed to update amount');
      }
    } catch (error) {
      console.error('Error updating amount:', error);
      toast.error('Failed to update amount');
    }
  };

  const handleBulkUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = new URLSearchParams();
      
      // Add bookings data as a JSON string
      const bookingsData = Object.keys(bulkAmounts).map(bookingId => ({
        booking_id: bookingId,
        amount: bulkAmounts[bookingId]
      }));
      
      console.log('Sending bookings data:', bookingsData); // Debug log
      data.append('bookings', JSON.stringify(bookingsData));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/agentStatement/updateMultipleAmounts`, {
        method: 'POST',
        headers: {
          'Authorization': token
        },
        body: data
      });

      const result = await response.json();
      if (result.status) {
        toast.success(result.message);
        // Refresh statement
        handleGenerateStatement();
        setBulkEditMode(false);
      } else {
        toast.error(result.message || 'Failed to update amounts');
      }
    } catch (error) {
      console.error('Error bulk updating amounts:', error);
      toast.error('Failed to update amounts');
    }
  };

  const handleGenerateExcel = async () => {
    if (!statement) {
      toast.error('Please generate statement first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const data = new URLSearchParams();
      data.append('agency_id', selectedAgency);
      data.append('from_date', fromDate);
      data.append('to_date', toDate);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/agentStatement/generateExcelStatement`, {
        method: 'POST',
        headers: {
          'Authorization': token
        },
        body: data
      });

      const result = await response.json();
      if (result.status) {
        // Download the file
        const link = document.createElement('a');
        link.href = result.download_url;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Excel file downloaded successfully');
      } else {
        toast.error(result.message || 'Failed to generate Excel file');
      }
    } catch (error) {
      console.error('Error generating Excel:', error);
      toast.error('Failed to generate Excel file');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  if (!hasPermission('agentstatement')) {
    return (
      <div className="dashboard">
        <CommonHeader />
        <div className="container mt-4">
          <div className="alert alert-danger">
            You don't have permission to access this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <CommonHeader />
      <div className="container mt-4">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h4>Agent Statement</h4>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="form-label">Agency</label>
                    <select 
                      className="form-select" 
                      value={selectedAgency} 
                      onChange={(e) => setSelectedAgency(e.target.value)}
                    >
                      <option value="">Select Agency</option>
                      {agencies.map(agency => (
                        <option key={agency.agency_id} value={agency.agency_id}>
                          {agency.agency_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">From Date</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={fromDate} 
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">To Date</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={toDate} 
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">&nbsp;</label>
                    <div>
                      <button 
                        className="btn btn-primary me-2" 
                        onClick={handleGenerateStatement}
                        disabled={loading}
                      >
                        {loading ? 'Generating...' : 'Generate Statement'}
                      </button>
                      {statement && (
                        <button 
                          className="btn btn-success" 
                          onClick={handleGenerateExcel}
                        >
                          Download Excel
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {statement && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <div className="alert alert-info">
                        <strong>Agency:</strong> {statement.agency_name} | 
                        <strong> Period:</strong> {formatDate(statement.from_date)} to {formatDate(statement.to_date)} | 
                        <strong> Total Bookings:</strong> {statement.total_bookings} | 
                        <strong> Total Amount:</strong> ₹{statement.total_amount?.toFixed(2)}
                      </div>
                    </div>
                  </div>
                )}

                {statement && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <button 
                        className="btn btn-warning me-2" 
                        onClick={() => setBulkEditMode(!bulkEditMode)}
                      >
                        {bulkEditMode ? 'Cancel Bulk Edit' : 'Bulk Edit Amounts'}
                      </button>
                      {bulkEditMode && (
                        <button 
                          className="btn btn-success" 
                          onClick={handleBulkUpdate}
                        >
                          Save All Changes
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {statement && statement.statement && statement.statement.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-striped table-bordered">
                      <thead className="table-dark">
                        <tr>
                          <th>Sl No</th>
                          <th>Pax Name</th>
                          <th>Contact No</th>
                          <th>Pickup Date</th>
                          <th>Pickup Point</th>
                          <th>Drop Point</th>
                          <th>Bus Time</th>
                          <th>Amount</th>
                          <th>Added By</th>
                          <th>Booking Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statement.statement.map((booking, index) => (
                          <tr key={booking.booking_id}>
                            <td>{index + 1}</td>
                            <td>{booking.pax_name}</td>
                            <td>{booking.pax_contact_no}</td>
                            <td>{formatDate(booking.pickup_date)}</td>
                            <td>{booking.pickup_point_name}</td>
                            <td>{booking.drop_point_name}</td>
                            <td>{booking.time_name}</td>
                            <td style={{ minWidth: '120px' }}>
                              {bulkEditMode ? (
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={bulkAmounts[booking.booking_id] || ''}
                                  onChange={(e) => setBulkAmounts(prev => ({
                                    ...prev,
                                    [booking.booking_id]: e.target.value
                                  }))}
                                  step="0.01"
                                  min="0"
                                  style={{ width: '80px' }}
                                />
                              ) : editingAmount === booking.booking_id ? (
                                <div className="input-group input-group-sm" style={{ width: '120px' }}>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={editAmount}
                                    onChange={(e) => setEditAmount(e.target.value)}
                                    step="0.01"
                                    min="0"
                                    style={{ width: '60px' }}
                                  />
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => handleUpdateAmount(booking.booking_id, editAmount)}
                                    style={{ padding: '2px 6px' }}
                                  >
                                    ✓
                                  </button>
                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => {
                                      setEditingAmount(null);
                                      setEditAmount('');
                                    }}
                                    style={{ padding: '2px 6px' }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <span style={{ fontWeight: 'bold', color: '#28a745' }}>₹{parseFloat(booking.amount || 0).toFixed(2)}</span>
                                  <button
                                    className="btn btn-sm btn-outline-primary ms-2"
                                    onClick={() => {
                                      setEditingAmount(booking.booking_id);
                                      setEditAmount(booking.amount || '');
                                    }}
                                    style={{ padding: '2px 8px', fontSize: '11px' }}
                                  >
                                    Edit
                                  </button>
                                </div>
                              )}
                            </td>
                            <td>{booking.added_user}</td>
                            <td>{formatDate(booking.booking_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : statement && statement.statement && statement.statement.length === 0 ? (
                  <div className="alert alert-warning">
                    No bookings found for the selected criteria.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentStatement;
